import type { ComponentType, ReactElement, ReactNode } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

import { openRevealHookModal } from "../../services/modal/openRevealHookModal";

import type { IContactRevealButtonStateBaseProps } from "./ContactRevealButtonState";
import ContactRevealButtonState from "./ContactRevealButtonState";
import { StyledContactDetailsOverlayFlex } from "./styles";

interface IProps extends IContactRevealButtonStateBaseProps {
    isHideActions?: boolean;
    hasDetailsToReveal: boolean;
    isContactsAvailabilityReadonly: boolean;
    isContactIdNotRevealed: boolean;
    isRevealed: boolean;
    actionsWrapperComponent?: ComponentType<{ children: ReactNode }>;
    textWrapperComponent?: ComponentType<{ children: ReactNode }>;
}

const ContactDetailsOverlayState = ({
    isHideActions,
    hasDetailsToReveal,
    isContactIdNotRevealed,
    isContactsAvailabilityReadonly,
    isRevealed,
    actionsWrapperComponent: ActionsWrapperComponent = StyledContactDetailsOverlayFlex,
    textWrapperComponent: TextWrapperComponent = StyledContactDetailsOverlayFlex,
    ...revealButtonProps
}: IProps): ReactElement | null => {
    const { buttonType, remainingRevealQuota, contactsCost } = revealButtonProps;

    const remainingRevealItems = Math.floor(remainingRevealQuota / contactsCost);
    const isNotEnoughCredits = remainingRevealItems <= 0;

    const handleGainAccess = () => {
        openRevealHookModal();
    };

    // purple crown button for trial users
    if (isContactsAvailabilityReadonly && isNotEnoughCredits && !isRevealed) {
        return (
            <ActionsWrapperComponent>
                <IconButton
                    type={buttonType === "flat" ? "flatTrial" : "trial"}
                    iconName="sparkles-gray"
                    iconSize="xs"
                    onClick={handleGainAccess}
                >
                    {translate("si.advanced_search.results_section.contacts.button.reveal")}
                </IconButton>
            </ActionsWrapperComponent>
        );
    }

    // error state if reveal process failed
    if (!isHideActions && isContactIdNotRevealed) {
        return (
            <ActionsWrapperComponent>
                <Tooltip
                    content={
                        <span>
                            {translate("si.advanced_search.results_section.contacts.button.reveal_failed_tooltip")}
                        </span>
                    }
                >
                    <div>
                        <SWReactIcons
                            iconColor={colorsPalettes.red["400"]}
                            iconName="alert-circle"
                            size="sm"
                        />
                    </div>
                </Tooltip>
            </ActionsWrapperComponent>
        );
    }

    // grey reveal button
    if (!hasDetailsToReveal) {
        return (
            <TextWrapperComponent>
                <Text
                    fontVariant="button"
                    color={colorsPalettes.carbon["200"]}
                >
                    {translate("si.advanced_search.results_section.contacts.button.no_details")}
                </Text>
            </TextWrapperComponent>
        );
    }

    // main blue reveal button
    if (!isHideActions && !isRevealed) {
        return (
            <ActionsWrapperComponent>
                <ContactRevealButtonState {...revealButtonProps} />
            </ActionsWrapperComponent>
        );
    }

    return null;
};

export default ContactDetailsOverlayState;
